import { useMemo } from "react";
import { Target, TrendingUp, AlertTriangle, Zap } from "lucide-react";

function extractTopInsights(content, mode) {
  if (!content) return null;

  const scoreMatch = content.match(/(\d{1,3})\s*\/\s*100/i)
    || content.match(/(?:score|rating)[:\s]*(\d{1,3})/i)
    || content.match(/## Priority Score[\s\S]*?(\d{1,3})/i);
  const score = scoreMatch ? Math.min(parseInt(scoreMatch[1], 10), 100) : null;

  const countBullets = (header) => {
    const re = new RegExp(`## ${header}[\\s\\S]*?(?=\\n## |$)`, "i");
    const m = content.match(re);
    return m ? (m[0].match(/^[-*]\s/gm) || []).length : 0;
  };

  const sections = [];
  const re = /^## (.+)$/gm;
  let m;
  while ((m = re.exec(content)) !== null) sections.push(m[1].trim());

  const opportunities = countBullets("Quick Wins") + countBullets("Missed Opportunities") + countBullets("Growth Opportunities") + countBullets("Key opportunities");
  const challenges = countBullets("Red Flags") + countBullets("Revenue Leaks") + countBullets("Common Problems") + countBullets("Key challenges");

  let action = "Review insights and take action";
  if (mode === "LEAD_SCORING") {
    action = score >= 70 ? "High priority — engage immediately" : score >= 40 ? "Moderate — nurture and follow up" : "Low priority — qualify further";
  } else if (mode === "CALL_PREP") {
    action = "Practice pitch and prep for objections";
  } else if (mode === "EMAIL_GENERATOR") {
    action = "Customize and send emails today";
  } else if (mode === "OPPORTUNITY_DETECTOR") {
    action = "Address quick wins first for fast ROI";
  } else if (mode === "COMPETITOR_ANALYSIS") {
    action = "Position against competitor weaknesses";
  }

  return { score, sections: sections.length, opportunities, challenges, action };
}

export default function ReportSummary({ content, mode, industry, companyName }) {
  const insights = useMemo(() => extractTopInsights(content, mode), [content, mode]);

  if (!insights) return null;

  const cards = [
    { label: "Sections", value: insights.sections, icon: Target, color: "text-blue-600 bg-blue-50" },
    { label: "Opportunities", value: insights.opportunities, icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
    { label: "Risks/Gaps", value: insights.challenges, icon: AlertTriangle, color: "text-amber-600 bg-amber-50" },
  ];

  if (insights.score != null) {
    cards.unshift({
      label: "Lead Score",
      value: `${insights.score}/100`,
      icon: Zap,
      color: insights.score >= 70 ? "text-emerald-600 bg-emerald-50" : insights.score >= 40 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50"
    });
  }

  return (
    <div className="bg-gradient-to-r from-slate-50 to-blue-50/50 rounded-xl border border-slate-200 p-5 space-y-4" data-testid="report-summary">
      <div>
        <h3 className="font-heading text-lg font-semibold text-slate-800">
          {companyName ? `${companyName} — ` : ""}{industry} Analysis
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">Quick summary of report findings</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-lg border border-slate-100 p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.color}`}>
                <Icon className="w-4.5 h-4.5" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800 leading-none">{c.value}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{c.label}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 bg-white/80 rounded-lg border border-slate-100 px-4 py-2.5">
        <Zap className="w-4 h-4 text-primary flex-shrink-0" />
        <p className="text-sm text-slate-700"><span className="font-medium">Recommended Action:</span> {insights.action}</p>
      </div>
    </div>
  );
}
